import { useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { APP_STATUSES, STATUS_ORDER, statusInfo } from "@/constants/statuses";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Phone,
  MapPin,
  User,
  Hash,
  StickyNote,
  Calendar,
  ChevronDown,
  ChevronUp,
  ArrowDownUp,
  X,
} from "lucide-react";

function MeterCard({ meter, onUpdate }) {
  const [open, setOpen] = useState(false);
  const info = statusInfo(meter.statut);
  const appInfo = meter.statut_app ? statusInfo(meter.statut_app) : null;

  const call = () => {
    if (!meter.telephone) return toast.error("Aucun numéro renseigné");
    toast.success(`Appel en cours vers ${meter.telephone}`);
    window.location.href = `tel:${meter.telephone}`;
  };

  const patch = async (payload, msg) => {
    try {
      const res = await api.patch(`/meters/${meter.id}`, payload);
      onUpdate(res.data);
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error("Échec de la mise à jour");
    }
  };

  const changeRdv = (val) => patch({ rdv_date: val }, "Rendez-vous enregistré");
  const saveNotes = (val) => patch({ notes: val });

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/60 hover:border-slate-700 transition-colors" data-testid="meter-card">
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <User className="h-3.5 w-3.5 text-slate-500 shrink-0" />
              <p className="font-semibold text-sm truncate">{meter.nom_famille || "Client inconnu"}</p>
            </div>
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1.5 truncate">
              <MapPin className="h-3 w-3 shrink-0" />
              {[meter.rue, meter.numero].filter(Boolean).join(" ")} · {meter.code_postal} {meter.lieu}
            </p>
            <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1.5 font-mono-data">
              <Hash className="h-3 w-3" /> {meter.ref || meter.numero_serie || "—"}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${info.chip} whitespace-nowrap`} data-testid="meter-excel-badge">
              {info.short}
            </span>
            {appInfo && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${appInfo.chip} whitespace-nowrap`} data-testid="meter-app-badge">
                {appInfo.short}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" onClick={call} data-testid="meter-card-call-button" className="flex-1 h-8 bg-[#00A896] hover:bg-[#028090] text-[#04141a] font-semibold text-xs">
            <Phone className="h-3.5 w-3.5 mr-1.5" />
            {meter.telephone || "Appeler"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} data-testid="meter-card-expand-button" className="h-8 px-2 border-slate-700 bg-transparent">
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        {open && (
          <div className="mt-3 space-y-3 pt-3 border-t border-slate-800">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">
                Statut Excel <span className="normal-case text-slate-600">(extraction)</span>
              </label>
              <div className="h-8 mt-1 flex items-center gap-2 px-3 rounded-md bg-slate-950/40 border border-slate-800 text-xs text-slate-300" data-testid="meter-excel-status-readonly">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: info.color }} />
                {info.label}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Statut app</label>
              <Select value={meter.statut_app || "none"} onValueChange={(v) => patch({ statut_app: v }, "Statut app mis à jour")}>
                <SelectTrigger className="h-8 mt-1 bg-slate-950/60 border-slate-700 text-xs" data-testid="meter-app-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" className="text-xs">Aucun</SelectItem>
                  {APP_STATUSES.map((k) => (
                    <SelectItem key={k} value={k} className="text-xs">
                      {statusInfo(k).label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Rendez-vous
              </label>
              <Input type="datetime-local" defaultValue={meter.rdv_date || ""} onBlur={(e) => e.target.value && changeRdv(e.target.value)} data-testid="meter-rdv-input" className="h-8 mt-1 bg-slate-950/60 border-slate-700 text-xs" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <StickyNote className="h-3 w-3" /> Notes
              </label>
              <Textarea defaultValue={meter.notes || ""} onBlur={(e) => saveNotes(e.target.value)} data-testid="meter-notes-input" className="mt-1 bg-slate-950/60 border-slate-700 text-xs min-h-[70px]" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const SORT_OPTIONS = [
  { key: "statut", label: "Statut" },
  { key: "rue", label: "Rue" },
  { key: "serie", label: "Identifiant" },
];

export default function MeterPanel({ meters, title, onUpdate, onClear, showClear }) {
  const [sortBy, setSortBy] = useState("statut");
  const sorted = useMemo(() => {
    const arr = [...meters];
    const cmp = (a, b, sel) => (sel(a) || "").localeCompare(sel(b) || "", "fr", { numeric: true, sensitivity: "base" });
    if (sortBy === "nom") arr.sort((a, b) => cmp(a, b, (m) => m.nom_famille));
    else if (sortBy === "rue") arr.sort((a, b) => cmp(a, b, (m) => `${m.rue || ""} ${m.numero || ""}`));
    else if (sortBy === "serie") arr.sort((a, b) => cmp(a, b, (m) => m.ref || m.numero_serie));
    else arr.sort((a, b) => STATUS_ORDER.indexOf(a.statut) - STATUS_ORDER.indexOf(b.statut) || cmp(a, b, (m) => m.nom_famille));
    return arr;
  }, [meters, sortBy]);

  return (
    <div className="h-full flex flex-col" data-testid="meter-panel">
      <div className="px-4 py-3 border-b border-slate-800 shrink-0 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-sm">{title}</h3>
            <p className="text-xs text-slate-500 mt-0.5" data-testid="panel-count">
              {meters.length} compteur{meters.length > 1 ? "s" : ""}
            </p>
          </div>
          {showClear && (
            <Button size="sm" variant="ghost" onClick={onClear} data-testid="panel-clear-selection" className="h-7 text-xs text-slate-400 hover:text-slate-100">
              <X className="h-3.5 w-3.5 mr-1" /> Tout afficher
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ArrowDownUp className="h-3.5 w-3.5 text-slate-500 shrink-0" />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-8 bg-slate-950/60 border-slate-700 text-xs" data-testid="panel-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.key} value={o.key} className="text-xs">
                  Trier par : {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {sorted.length === 0 ? (
          <div className="text-center text-slate-500 text-sm mt-12 px-6">
            Cliquez sur une bulle de la carte pour afficher les compteurs de la zone.
          </div>
        ) : (
          sorted.map((m) => <MeterCard key={m.id} meter={m} onUpdate={onUpdate} />)
        )}
      </div>
    </div>
  );
}
